import { type ReactNode, type FC } from 'react'
import classNames from 'classnames'
import { Icon, SpinnerLoader, Tooltip, Typography } from 'shared/ui'
import { makeTextFieldError, TextFieldPlaceholder } from 'shared/ui/TextField'
import { numberFormat } from 'shared/lib'
import type { IResponseInvalidBreakdown } from 'entities/Broadcast/api/types'
import styles from './styles.module.scss'

type IUiContactsTriggerProps = {
  total: number | null
  skipped: number
  invalidBreakdown: IResponseInvalidBreakdown | null
  loading?: boolean
  onClick?: () => void
  error?: ReactNode
  viewOnly?: boolean
  wrapPlaceholderClassName?: string
  showZeroState?: boolean
}

type ITooltipTriggerProps = {
  children: ReactNode
  total: number | null
  skipped: number
  invalidBreakdown: IResponseInvalidBreakdown | null
}

type ITooltipInfo = {
  key: string
  label: string
  count: string
}

const TooltipTrigger: FC<ITooltipTriggerProps> = ({
  children,
  total,
  skipped,
  invalidBreakdown,
}) => {
  if (!total) return <>{children}</>

  const all_count = numberFormat({ value: total })
  const invalid_count = numberFormat({ value: skipped })

  const breakdownLabels: Record<keyof IResponseInvalidBreakdown, string> = {
    invalid_reason: 'Invalid',
    opted_out: 'Unsubscribed',
    blocked: 'Blocked',
    not_allowed: 'Not allowed',
    no_number: 'Undefined sender number',
    unknown: 'Unknown reason',
  }

  const breakdownKeys: Array<keyof IResponseInvalidBreakdown> = [
    'invalid_reason',
    'opted_out',
    'blocked',
    'not_allowed',
    'no_number',
    'unknown',
  ]

  const breakdownList: ITooltipInfo[] = []

  if (invalidBreakdown !== null && invalidBreakdown !== undefined) {
    breakdownKeys.forEach((breakdownKey) => {
      const value = invalidBreakdown[breakdownKey]

      if (value)
        breakdownList.push({
          key: breakdownKey,
          label: breakdownLabels[breakdownKey],
          count: numberFormat({ value }),
        })
    })
  }

  return (
    <Tooltip
      type={'filtered_items'}
      placement={'top-start'}
      label={'Total contacts that meet the filters criteria'}
      items={[
        {
          title: 'Total',
          total: all_count,
          dropdown: 'Total contacts that meet the filters criteria.',
        },
        {
          title: 'Skipped',
          total: invalid_count,
          dropdown: (
            <div className={styles.tooltipContentList}>
              {breakdownList?.map(({ key, label, count }) => {
                return (
                  <Typography
                    key={key}
                    variant={'body-sm-regular'}
                    ariaLabel={label}
                    color={'var(--content-primary-tertiary-inverted)'}
                  >
                    {label}:
                    <Typography
                      variant={'body-sm-regular'}
                      ariaLabel={`${label} count`}
                      color={'var(--content-primary-primary-inverted)'}
                      className={styles.tooltipContentCount}
                    >
                      {count}
                    </Typography>
                  </Typography>
                )
              })}

              <Typography
                variant={'body-sm-regular'}
                ariaLabel={'Skipped description'}
                color={'var(--content-primary-tertiary-inverted)'}
                className={classNames(styles.tooltipContentDesc, {
                  [styles.last]: !!breakdownList.length,
                })}
              >
                Contacts that are invalid, unsubscribed, blocked, or not allowed are skipped
                automatically.
              </Typography>
            </div>
          ),
        },
      ]}
      className={styles.tooltip}
      PopperProps={{ className: styles['tooltip-content'] }}
      marginLeft={-3}
      padding={'12px 8px'}
    >
      <>{children}</>
    </Tooltip>
  )
}

export const UiContactsTrigger = ({
  total,
  skipped,
  onClick,
  error,
  loading,
  viewOnly,
  wrapPlaceholderClassName,
  showZeroState,
  invalidBreakdown,
}: IUiContactsTriggerProps) => {
  const content = () => {
    if (loading) {
      return (
        <div className={styles.loaderTrigger}>
          <SpinnerLoader
            ariaLabel={'UiContactsTrigger'}
            size={10}
            indeterminatecolor={'var(--content-primary-tertiary)'}
          />
        </div>
      )
    }

    if (total) {
      const all_count = numberFormat({ value: total })
      const count = numberFormat({ value: total - skipped })

      return (
        <Typography className={styles.text} variant={'text-input-field'} ariaLabel={'number'}>
          {count} <span>of {all_count}</span>
        </Typography>
      )
    }

    if (showZeroState) {
      return (
        <Typography className={styles.text} variant={'text-input-field'} ariaLabel={'number'}>
          0 <span>of 0</span>
        </Typography>
      )
    }

    return (
      <Typography
        variant={'text-input-field'}
        color={'var(--content-neutral-primary)'}
        ariaLabel={'SelectContacts'}
      >
        Select contacts
      </Typography>
    )
  }

  return (
    <TooltipTrigger total={total} skipped={skipped} invalidBreakdown={invalidBreakdown}>
      <div onClick={onClick} className={styles.trigger}>
        <TextFieldPlaceholder
          placeholder={
            <div className={classNames(styles.wrapPlaceholder, wrapPlaceholderClassName)}>
              <div className={styles.trigger__icon}>
                <Icon icon={'contacts'} tertiary />
              </div>

              {content()}
            </div>
          }
          variant={'outline'}
          size={'small'}
          className={styles.input}
          rightActions={[
            makeTextFieldError({
              placement: 'right',
            }),
          ]}
          error={error}
          viewOnly={viewOnly}
        />
      </div>
    </TooltipTrigger>
  )
}
